import { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Upload, FileText, Download, AlertTriangle, Check, X, ChevronDown, ChevronUp,
  Users, MapPin, Headphones, FileSpreadsheet
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CSV_TEMPLATE = `email,name,company_name,phone,location_name,location_address,kassensystem_status,kassensystem_provider,kartenzahlung_status,kartenzahlung_provider,reservierung_status,reservierung_provider,lieferdienste_status,lieferdienste_provider,personalplanung_status,personalplanung_provider,buchhaltung_status,buchhaltung_provider,warenwirtschaft_status,warenwirtschaft_provider,kundenbindung_status,kundenbindung_provider,service_support,service_business_analysis,service_social_media,service_website
max@restaurant-sonne.de,Max Mustermann,Restaurant Sonnenschein,+4915112345678,Hauptstandort,Musterstr. 1 12345 Berlin,active,orderbird,active,SumUp,in_consultation,resmio,not_active,,not_active,,active,lexoffice,not_active,,not_active,,true,false,false,true
lisa@cafe-central.de,Lisa Schmidt,Café Central,+4917687654321,Filiale Nord,Hauptstr. 55 80331 München,active,gastrofix,active,Sumup,active,OpenTable,active,Lieferando,in_consultation,,not_active,,not_active,,active,quandoo,true,true,false,false`;

const AdminCSVImport = () => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [changes, setChanges] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [exporting, setExporting] = useState(null); // Track which export is in progress

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setPreviewData(null);
      setChanges([]);
      setResult(null);
    } else {
      alert('Bitte wähle eine CSV-Datei aus.');
    }
  };

  const handleParseCSV = async () => {
    if (!file) return;
    
    setParsing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreviewData(response.data.preview);
      setChanges(response.data.changes);
    } catch (error) {
      console.error('Failed to parse CSV:', error);
      alert(error.response?.data?.detail || 'Fehler beim Parsen der CSV-Datei');
    } finally {
      setParsing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/import/execute`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      setFile(null);
      setPreviewData(null);
      setChanges([]);
    } catch (error) {
      console.error('Failed to import:', error);
      alert(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'kunden_import_vorlage.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const downloadExport = async (exportType) => {
    setExporting(exportType);
    try {
      // Get token from localStorage for authenticated request
      const token = localStorage.getItem('token');
      
      // Use fetch instead of axios for better blob handling
      const response = await fetch(`${API}/admin/export/${exportType}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportType}_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error('Failed to export:', error);
      alert('Export fehlgeschlagen: ' + error.message);
    } finally {
      setExporting(null);
    }
  };

  const toggleRow = (index) => {
    setExpandedRows(prev => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-csv-import">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-ink">Daten Import & Export</h1>
        <p className="text-ink/80 mt-1">
          Importiere und exportiere Kundendaten, Standorte und Systemkonfigurationen.
        </p>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-none border-2 border-ink p-6 mb-6">
        <h2 className="font-semibold text-ink mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-purple" />
          Daten exportieren
        </h2>
        <p className="text-lg text-ink/80 mb-4">
          Exportiere deine Daten als CSV-Datei für Backups, Analysen oder externe Verwendung.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Users Export */}
          <div className="border-2 border-ink rounded-none p-4 hover:border-purple hover:bg-purple/5 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-none bg-lilac flex items-center justify-center">
                <Users className="w-5 h-5 text-purple" />
              </div>
              <div>
                <h3 className="font-medium text-ink">Kunden</h3>
                <p className="text-xs text-ink/60">Kontaktdaten & Status</p>
              </div>
            </div>
            <Button 
              onClick={() => downloadExport('users')} 
              disabled={exporting === 'users'}
              variant="outline" 
              size="sm" 
              className="w-full"
              data-testid="export-users-btn"
            >
              {exporting === 'users' ? (
                <span className="animate-pulse">Exportiere...</span>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV herunterladen
                </>
              )}
            </Button>
          </div>

          {/* Systems Export */}
          <div className="border-2 border-ink rounded-none p-4 hover:border-purple hover:bg-purple/5 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-none bg-lilac flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple" />
              </div>
              <div>
                <h3 className="font-medium text-ink">Gastro-Systeme</h3>
                <p className="text-xs text-ink/60">Alle Systemkonfigurationen</p>
              </div>
            </div>
            <Button 
              onClick={() => downloadExport('systems')} 
              disabled={exporting === 'systems'}
              variant="outline" 
              size="sm" 
              className="w-full"
              data-testid="export-systems-btn"
            >
              {exporting === 'systems' ? (
                <span className="animate-pulse">Exportiere...</span>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV herunterladen
                </>
              )}
            </Button>
          </div>

          {/* Services Export */}
          <div className="border-2 border-ink rounded-none p-4 hover:border-purple hover:bg-purple/5 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-none bg-green-100 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-ink">Service-Abos</h3>
                <p className="text-xs text-ink/60">Gebuchte Services</p>
              </div>
            </div>
            <Button 
              onClick={() => downloadExport('services')} 
              disabled={exporting === 'services'}
              variant="outline" 
              size="sm" 
              className="w-full"
              data-testid="export-services-btn"
            >
              {exporting === 'services' ? (
                <span className="animate-pulse">Exportiere...</span>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV herunterladen
                </>
              )}
            </Button>
          </div>

          {/* Consultations Export */}
          <div className="border-2 border-ink rounded-none p-4 hover:border-purple hover:bg-purple/5 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-none bg-amber-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium text-ink">Erstberatungen</h3>
                <p className="text-xs text-ink/60">Beratungsanfragen</p>
              </div>
            </div>
            <Button 
              onClick={() => downloadExport('consultations')} 
              disabled={exporting === 'consultations'}
              variant="outline" 
              size="sm" 
              className="w-full"
              data-testid="export-consultations-btn"
            >
              {exporting === 'consultations' ? (
                <span className="animate-pulse">Exportiere...</span>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  CSV herunterladen
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Import Section Header */}
      <div className="flex items-center gap-2 mb-4">
        <Upload className="w-5 h-5 text-purple" />
        <h2 className="font-semibold text-ink">Daten importieren</h2>
      </div>

      {/* Template Download */}
      <div className="bg-white rounded-none border-2 border-ink p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-none bg-purple/10 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-purple" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-ink mb-1">CSV-Vorlage</h2>
            <p className="text-lg text-ink/80 mb-3">
              Lade die Vorlage herunter, um das korrekte Format zu sehen. Die Datei enthält alle verfügbaren Felder.
            </p>
            <Button onClick={downloadTemplate} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Vorlage herunterladen
            </Button>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-off rounded-none">
          <h3 className="text-sm font-medium text-ink/80 mb-2">Verfügbare Felder:</h3>
          <div className="grid md:grid-cols-2 gap-2 text-xs text-ink/80">
            <div><strong>Kontakt:</strong> email*, name*, company_name*, phone</div>
            <div><strong>Standort:</strong> location_name, location_address</div>
            <div><strong>Systeme:</strong> [kategorie]_status, [kategorie]_provider</div>
            <div><strong>Services:</strong> service_support, service_business_analysis, etc.</div>
          </div>
          <p className="text-xs text-ink/60 mt-2">
            * Pflichtfelder | Status-Werte: not_active, in_consultation, active | Service-Werte: true, false
          </p>
        </div>
      </div>

      {/* File Upload */}
      <div className="bg-white rounded-none border-2 border-ink p-6 mb-6">
        <h2 className="font-semibold text-ink mb-4">Datei hochladen</h2>
        
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-ink/30 rounded-none p-8 text-center cursor-pointer hover:border-purple transition-colors"
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-8 h-8 text-purple" />
              <div className="text-left">
                <p className="font-medium text-ink">{file.name}</p>
                <p className="text-sm text-ink/60">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); setFile(null); setPreviewData(null); setChanges([]); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-ink/60 mx-auto mb-3" />
              <p className="text-ink/80">Klicke hier oder ziehe eine CSV-Datei hierher</p>
              <p className="text-sm text-ink/60 mt-1">Nur .csv Dateien</p>
            </>
          )}
        </div>
        
        {file && !previewData && (
          <div className="mt-4 flex justify-end">
            <Button onClick={handleParseCSV} disabled={parsing} className="">
              {parsing ? 'Wird analysiert...' : 'Datei analysieren'}
            </Button>
          </div>
        )}
      </div>

      {/* Preview & Changes */}
      {previewData && changes.length > 0 && (
        <div className="bg-white rounded-none border-2 border-ink p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="font-semibold text-ink">Änderungen bestätigen</h2>
          </div>
          
          <p className="text-lg text-ink/80 mb-4">
            Folgende Änderungen werden vorgenommen. Bitte überprüfe diese sorgfältig.
          </p>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {changes.map((change, index) => (
              <div key={index} className="border-2 border-ink rounded-none overflow-hidden">
                <button
                  onClick={() => toggleRow(index)}
                  className="w-full p-4 flex items-center justify-between bg-off hover:bg-off transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-none ${
                      change.type === 'new' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {change.type === 'new' ? 'NEU' : 'UPDATE'}
                    </span>
                    <span className="font-medium text-ink">{change.email}</span>
                    <span className="text-ink/60">{change.company_name}</span>
                  </div>
                  {expandedRows[index] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                {expandedRows[index] && (
                  <div className="p-4 bg-white">
                    {change.type === 'new' ? (
                      <p className="text-sm text-ink/80">
                        Neuer Kunde wird angelegt und erhält eine Einladungs-E-Mail.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {change.fields?.map((field, fIndex) => (
                          <div key={fIndex} className="text-sm grid grid-cols-3 gap-2">
                            <span className="text-ink/60">{field.name}:</span>
                            <span className="text-red-500 line-through">{field.old || '(leer)'}</span>
                            <span className="text-green-600">{field.new || '(leer)'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={() => { setPreviewData(null); setChanges([]); }}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleConfirmImport} 
              disabled={importing}
              className=""
            >
              {importing ? 'Importiere...' : `${changes.length} Änderungen bestätigen`}
            </Button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-none p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-none bg-green-100 flex items-center justify-center">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="font-semibold text-green-800">Import erfolgreich!</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white rounded-none p-3">
              <p className="text-ink/60">Neue Kunden</p>
              <p className="text-2xl font-bold text-ink">{result.created}</p>
            </div>
            <div className="bg-white rounded-none p-3">
              <p className="text-ink/60">Aktualisiert</p>
              <p className="text-2xl font-bold text-ink">{result.updated}</p>
            </div>
            <div className="bg-white rounded-none p-3">
              <p className="text-ink/60">Einladungen gesendet</p>
              <p className="text-2xl font-bold text-ink">{result.invitations_sent}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCSVImport;
